import { useEffect, useRef, useState } from 'react';
import { Icon } from '@/components/ui/Icon/Icon';
import { Button } from '@/components/ui/Button/Button';
import { Dialog } from '@/components/ui/Dialog/Dialog';
import { useSession } from '@/services/auth';
import { useUIStore } from '@/stores/uiStore';
import { useDeleteVedlikeholdEntry, useVedlikeholdEntries } from '@/services/vedlikehold';
import { nb } from '@/i18n/nb';
import type { VedlikeholdEntry } from '@/types/api';
import { EntryFormModal } from './EntryFormModal';
import { AssignPickerModal } from './AssignPickerModal';
import { StatusControlModal } from './StatusControlModal';
import { StatusBadge } from './StatusBadge';
import { ImageViewerOverlay } from './ImageViewerOverlay';
import s from './Vedlikehold.module.css';

/*
 * Dugnadsoppgaver — the communal-work task sub-card (ADR-0028). A FLAT list (no
 * kategorier, unlike Generelt), server-sorted updatedAt DESC, date hidden. Each task
 * shows description + contributor + optional image + a status badge, plus the assignee
 * name once it's not åpen (per dugnad-public-view).
 *
 * Two authz tiers drive the Rediger-mode affordances:
 *   - content add/edit/delete → own-or-owner+ (server canEdit/canDelete), same as Generelt
 *   - assign + status         → ANY logged-in user (the ADR-0028 deviation), so those
 *     buttons appear for everyone in edit mode, not just the task's creator
 * The server is authoritative in both cases; the UI only hides affordances.
 */
export function DugnadsOppgaver() {
  const { user } = useSession();
  const editMode = useUIStore((state) => state.editMode);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: tasks, isLoading, isError } = useVedlikeholdEntries('dugnad');
  const deleteMutation = useDeleteVedlikeholdEntry();

  const [addOpen, setAddOpen] = useState(false);
  const [editTask, setEditTask] = useState<VedlikeholdEntry | null>(null);
  const [assignTask, setAssignTask] = useState<VedlikeholdEntry | null>(null);
  const [statusTask, setStatusTask] = useState<VedlikeholdEntry | null>(null);
  const [deleteCandidate, setDeleteCandidate] = useState<VedlikeholdEntry | null>(null);

  // Move focus into the freshly opened sub-card (same pattern as Generelt + Hytteregler).
  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  const canContribute = user !== null;
  const showActions = editMode && canContribute;

  function handleDeleteConfirm() {
    if (deleteCandidate === null) return;
    deleteMutation.mutate(
      { id: deleteCandidate.id, section: 'dugnad' },
      { onSettled: () => setDeleteCandidate(null) },
    );
  }

  return (
    <div ref={containerRef} tabIndex={-1} className={s.generelt}>
      {showActions && (
        <div className={s.toolbar}>
          <Button variant="primary" onClick={() => setAddOpen(true)}>
            <Icon name="plus" size={16} /> {nb.vedlikehold.dugnad.add}
          </Button>
        </div>
      )}

      {isLoading && <p className={s.status} aria-live="polite" />}
      {isError && <p className={s.status}>{nb.vedlikehold.dugnad.loadError}</p>}

      {tasks && tasks.length === 0 && <p className={s.empty}>{nb.vedlikehold.dugnad.empty}</p>}

      {tasks && tasks.length > 0 && (
        <div className={s.entries}>
          {tasks.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              showActions={showActions}
              onEdit={setEditTask}
              onDelete={setDeleteCandidate}
              onAssign={setAssignTask}
              onStatus={setStatusTask}
            />
          ))}
        </div>
      )}

      {addOpen && (
        <EntryFormModal
          mode="add"
          section="dugnad"
          kategorier={[]}
          onClose={() => setAddOpen(false)}
        />
      )}

      {editTask && (
        <EntryFormModal
          mode="edit"
          section="dugnad"
          entry={editTask}
          kategorier={[]}
          onClose={() => setEditTask(null)}
        />
      )}

      {assignTask && <AssignPickerModal task={assignTask} onClose={() => setAssignTask(null)} />}

      {statusTask && <StatusControlModal task={statusTask} onClose={() => setStatusTask(null)} />}

      <Dialog
        open={deleteCandidate !== null}
        title={nb.vedlikehold.dugnadDeleteDialog.title}
        message={nb.vedlikehold.dugnadDeleteDialog.body}
        confirmLabel={nb.vedlikehold.dugnadDeleteDialog.confirm}
        cancelLabel={nb.common.cancel}
        confirmVariant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteCandidate(null)}
      />
    </div>
  );
}

function TaskRow({
  task,
  showActions,
  onEdit,
  onDelete,
  onAssign,
  onStatus,
}: {
  task: VedlikeholdEntry;
  showActions: boolean;
  onEdit: (t: VedlikeholdEntry) => void;
  onDelete: (t: VedlikeholdEntry) => void;
  onAssign: (t: VedlikeholdEntry) => void;
  onStatus: (t: VedlikeholdEntry) => void;
}) {
  const [viewerOpen, setViewerOpen] = useState(false);
  // status is dugnad-only and always set server-side; fall back to åpen defensively.
  const status = task.status ?? 'åpen';
  const isOpen = status === 'åpen';

  return (
    <article className={s.entry}>
      {task.image && (
        <>
          <button
            type="button"
            className={s.entryThumbButton}
            onClick={() => setViewerOpen(true)}
            aria-label={nb.vedlikehold.viewer.openAria}
          >
            <img
              className={s.entryThumb}
              src={task.image.thumbUrl}
              alt={nb.vedlikehold.dugnad.imageAlt}
              loading="lazy"
            />
          </button>
          <ImageViewerOverlay
            open={viewerOpen}
            onClose={() => setViewerOpen(false)}
            src={task.image.viewerUrl}
            alt={task.description}
          />
        </>
      )}
      <div className={s.entryBody}>
        <div className={s.taskTop}>
          <StatusBadge status={status} />
        </div>
        <p className={s.entryDescription}>{task.description}</p>
        <div className={s.entryMeta}>
          <span>
            {nb.vedlikehold.dugnad.contributedBy} {task.createdByName}
          </span>
          {!isOpen && task.assigneeName && (
            <span>
              {nb.vedlikehold.dugnad.assignedTo} {task.assigneeName}
            </span>
          )}
        </div>

        {showActions && (
          <div className={s.entryActions}>
            {/* Assign + status — open to ANY logged-in user (ADR-0028), so not gated
                on canEdit. An åpen task only needs assigning; an assigned task offers
                status changes + reassignment. */}
            {isOpen ? (
              <button type="button" className={s.entryActionBtn} onClick={() => onAssign(task)}>
                <Icon name="user" size={14} /> {nb.vedlikehold.dugnad.assign}
              </button>
            ) : (
              <>
                <button type="button" className={s.entryActionBtn} onClick={() => onStatus(task)}>
                  <Icon name="check" size={14} /> {nb.vedlikehold.dugnad.changeStatus}
                </button>
                <button type="button" className={s.entryActionBtn} onClick={() => onAssign(task)}>
                  <Icon name="user" size={14} /> {nb.vedlikehold.dugnad.reassign}
                </button>
              </>
            )}

            {/* Content edit/delete stays own-or-owner+ (server-derived flags). */}
            {task.canEdit && (
              <button type="button" className={s.entryActionBtn} onClick={() => onEdit(task)}>
                <Icon name="pencil" size={14} /> {nb.vedlikehold.dugnad.edit}
              </button>
            )}
            {task.canDelete && (
              <button
                type="button"
                className={s.entryActionBtn}
                data-variant="danger"
                onClick={() => onDelete(task)}
              >
                <Icon name="trash" size={14} /> {nb.vedlikehold.dugnad.delete}
              </button>
            )}
          </div>
        )}
      </div>
    </article>
  );
}
